// Aviso global de cierre de sesión (MT-047, H-018). Se monta una vez en `App`. Dos casos con el mismo diálogo:
// - Inactividad: un minuto antes del límite; «Seguir trabajando» (o cualquier actividad) lo cancela.
// - Vencimiento absoluto (la jornada de 12 h, configurable): 10 min antes; NO se puede prolongar, así que solo pide guardar y terminar.
// Si vence, cierra la sesión por el camino de siempre (`cerrarSesionHce`) y vuelve al login; las demás pestañas cierran también.
// No guarda borradores: el HCE no tiene un mecanismo común de borrador, así que el aviso dice claramente que lo no guardado se pierde.
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { anunciarCierre, CABECERA_ACTIVIDAD, olvidarActividad, useInactividad } from '@/lib/inactividad';
import { cerrarSesionHce } from './cerrarSesion';

export function AvisoInactividad(): JSX.Element | null {
  const profile = useMedplumProfile();
  return profile ? <AvisoInactividadActivo /> : null;
}

function AvisoInactividadActivo(): JSX.Element | null {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [absolutoVisto, setAbsolutoVisto] = useState(false);
  const cerrar = (origen: 'local' | 'otra-pestana' = 'local') => {
    olvidarActividad();
    if (origen === 'local') anunciarCierre();
    void cerrarSesionHce(medplum, navigate);
  };
  const { estado, segundos, absoluto, segundosAbsoluto, seguir } = useInactividad({
    activo: true,
    onVencido: cerrar,
    // `auth/me` con la cabecera de actividad: es lo único que renueva la inactividad en el servidor (el tráfico de fondo no), y devuelve sus límites.
    onLatido: () => medplum.get('auth/me', { cache: 'no-cache', headers: { [CABECERA_ACTIVIDAD]: '1' } }),
  });
  // Independientes: «Entendido» solo oculta el aviso del absoluto; el de inactividad (más urgente) sale igual y tiene prioridad.
  const esAbsoluto = estado !== 'aviso';
  if (esAbsoluto && (absoluto !== 'aviso' || absolutoVisto)) return null;
  return (
    <div role="alertdialog" aria-modal="true" aria-labelledby="aviso-inactividad-titulo" className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h2 id="aviso-inactividad-titulo" className="text-lg font-semibold">
          {t(esAbsoluto ? 'inactividad.tituloAbsoluto' : 'inactividad.titulo')}
        </h2>
        <p className="mt-2 text-sm">{esAbsoluto ? t('inactividad.mensajeAbsoluto', { minutos: Math.max(1, Math.ceil(segundosAbsoluto / 60)) }) : t('inactividad.mensaje', { segundos })}</p>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className="rounded border px-3 py-1.5 text-sm" onClick={() => cerrar('local')}>
            {t('inactividad.cerrarAhora')}
          </button>
          {esAbsoluto ? (
            <button type="button" autoFocus className="rounded bg-black px-3 py-1.5 text-sm text-white" onClick={() => setAbsolutoVisto(true)}>
              {t('inactividad.entendido')}
            </button>
          ) : (
            <button type="button" autoFocus className="rounded bg-black px-3 py-1.5 text-sm text-white" onClick={seguir}>
              {t('inactividad.seguir')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
